/**
 * Playtests (INVENTORY §1 screen 7, §4.5; prototype lines 409-498).
 *
 * Reads `selectors.playtestsVM(s, now)` for everything the template's `pt` bag
 * carried, plus `selectors.3e#ptSyncChip` for the sync-chip colours — the
 * prototype keyed those off `ptLinked` alone, so a failing revalidation still
 * painted a calm "LIVE ·" (INVENTORY §12.3). `syncLbl` itself is already the
 * freshness chip and cannot lie.
 *
 * `now` is re-read on every render and the 5 s `tick` in the ui slice is what
 * forces those renders, so "3 DAYS LEFT" / "CHECKED 12S AGO" stay honest
 * without a timer in here.
 */

import { useEffect, useRef } from 'react';

import { Avatar } from '../components/Avatar.tsx';
import { PtCover } from '../components/PtArt.tsx';
import { SearchBox } from '../components/SearchBox.tsx';
import { useQd } from '../store/index.ts';
import { removeChip } from '../store/selectors.3c.ts';
import { ptSyncChip } from '../store/selectors.3e.ts';
import { playtestsVM } from '../store/selectors.ts';

/** The 4-rect pixel triangle the strips and the MARK INTEREST button use. */
function Tri({ w = 7, h = 6 }: { w?: number; h?: number }) {
  return (
    <svg width={w} height={h} viewBox="0 0 9 8" style={{ display: 'block', flex: 'none' }} aria-hidden="true">
      <rect x="4" y="0" width="1" height="2" fill="currentColor" />
      <rect x="3" y="2" width="3" height="2" fill="currentColor" />
      <rect x="2" y="4" width="5" height="2" fill="currentColor" />
      <rect x="1" y="6" width="7" height="2" fill="currentColor" />
    </svg>
  );
}

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

/** INVENTORY §4.5 / §9 — line 496, verbatim product copy. */
export const PT_DATA_FOOTER =
  'DATA: STORE-WIDGET SCRAPE NIGHTLY · WAVES DETECTED FROM PLAYTEST-APP PLAYER COUNTS · GRANTED ACCESS FROM YOUR STEAM LIBRARY · REQUESTS SELF-REPORTED · OFF-STEAM ENTRIES USER-ADDED + ATTRIBUTED';

export default function Playtests(): JSX.Element {
  const s = useQd();
  const now = Date.now();
  const v = playtestsVM(s, now);
  const chip = ptSyncChip(s, now);

  /**
   * Fetch the Steam asset records for the cards on screen.
   *
   * Without this the art ladder has nothing but guesses to walk, and a playtest
   * is an unreleased game, so the legacy CDN shapes it can guess 404 — the
   * blank-thumbnail bug (see `logic.ts#ptArtCands` and `playtestsVM.appids`).
   * Read through a ref against a joined key, the `screens/Discover.tsx` idiom:
   * the selector mints a fresh array every render, so depending on the array
   * itself would re-queue on every paint. The per-appid `SURFACE_MIN_MS` floor in
   * `revalidateSurface` is what keeps a filter keystroke from hammering it.
   */
  const ptAppids = v.appids;
  const appidRef = useRef<number[]>(ptAppids);
  appidRef.current = ptAppids;
  const appidKey = ptAppids.join(',');
  useEffect(() => {
    if (appidRef.current.length) useQd.getState().queueAssets(appidRef.current);
  }, [appidKey]);

  const cell = (border: string, color: string, fs: string) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid ' + border,
    height: 44,
    fontFamily: MONO,
    fontSize: fs,
    color,
    cursor: 'pointer',
    boxSizing: 'border-box' as const,
  });

  return (
    <div data-screen-label="Playtests" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '16px 14px 0' }}>
        <div style={{ fontFamily: PIXEL, fontSize: 11, color: 'var(--gt-text)' }}>PLAYTESTS</div>
        <div style={{ fontFamily: MONO, fontSize: '9.5px', letterSpacing: '.04em', lineHeight: 1.7, color: 'var(--gt-muted)' }}>
          CLOSED BETAS + PLAYTESTS FOR UNRELEASED GAMES
        </div>
      </div>

      {/* 2×2 action grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, padding: '12px 14px 0' }}>
        <div
          onClick={s.openPtSync}
          style={{
            ...cell(chip.bd, chip.clr, '9.5px'),
            gap: 7,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
        >
          <span style={{ width: 7, height: 7, background: chip.dot, flex: 'none' }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{chip.lbl}</span>
        </div>
        <div onClick={s.openPtTracked} style={cell('var(--gt-border-2)', 'var(--gt-text-3)', '10px')}>
          ★ TRACKED ({v.trackedN})
        </div>
        <div onClick={s.openPtBrowse} style={cell('var(--gt-border-2)', 'var(--gt-text-3)', '10px')}>
          ⊞ BROWSE STEAM
        </div>
        <div onClick={s.openPtAdd} style={cell('var(--gt-text)', 'var(--gt-text)', '10px')}>
          + ADD PLAYTEST
        </div>
      </div>

      <div style={{ padding: '12px 14px 0' }}>
        <SearchBox
          value={v.q}
          onChange={(e) => s.setPtQ(e.target.value)}
          placeholder="SEARCH PLAYTESTS…"
          w="100%"
          ifs={12}
          pad="13px 12px"
        />
      </div>

      {/* Chips + SHOW/HIDE ENDED + the shared filter button and ITS chips */}
      <div
        data-hs="1"
        style={{
          display: 'flex',
          gap: 6,
          alignItems: 'center',
          overflowX: 'auto',
          padding: '10px 14px 0',
          fontFamily: MONO,
          fontSize: 10,
          letterSpacing: '.04em',
        }}
      >
        {v.chips.map((c) => (
          <span
            key={c.key}
            onClick={() => s.togglePtChip(c.id)}
            style={{
              border: '1px solid ' + c.bd,
              color: c.clr,
              background: c.bg,
              padding: '11px 10px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              flex: 'none',
            }}
          >
            {c.l}
          </span>
        ))}
        <span
          onClick={s.togglePtEnded}
          style={{
            border: '1px solid ' + v.endBd,
            color: v.endClr,
            background: v.endBg,
            padding: '11px 10px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            flex: 'none',
          }}
        >
          {v.endLbl}
        </span>
        <div style={{ width: 1, height: 22, background: 'var(--gt-border-2)', margin: '0 2px', flex: 'none' }} />
        <div
          onClick={() => s.toggleFltOpen()}
          style={{
            display: 'flex',
            alignItems: 'center',
            border: '1px solid ' + v.fltBtnBd,
            color: v.fltBtnClr,
            background: v.fltBtnBg,
            height: 40,
            padding: '0 13px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            flex: 'none',
            boxSizing: 'border-box',
          }}
        >
          {v.fltBtnTxt}
        </div>

        {/*
          The active-filter rail, inline after the trigger rather than on a row
          of its own — this row already scrolls horizontally, and the chips
          belong beside the button that opened them. Playtests was the last
          screen where an active filter was a number on a button and nothing
          else; now that every filter actually narrows this list, the rail is
          the only thing that says WHICH one shortened it.
        */}
        {v.fltChips.map((c) => (
          <div
            key={c.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              border: '1px solid ' + c.bd,
              color: c.clr,
              background: c.bg,
              height: 40,
              padding: '0 11px',
              whiteSpace: 'nowrap',
              flex: 'none',
              boxSizing: 'border-box',
            }}
          >
            {c.l}
            <span
              onClick={() => removeChip(useQd.getState(), c)}
              style={{ cursor: 'pointer', color: 'var(--gt-text-5)', padding: 2 }}
            >
              ✕
            </span>
          </div>
        ))}

        {v.fltN > 0 ? (
          <div
            onClick={() => s.clearFlt()}
            style={{
              color: 'var(--gt-faint)',
              cursor: 'pointer',
              padding: '11px 6px',
              whiteSpace: 'nowrap',
              flex: 'none',
            }}
          >
            CLEAR ALL
          </div>
        ) : null}
      </div>

      {/* Grouped list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '14px 0 20px', flex: 1 }}>
        {v.groups.map((g) => (
          <div key={g.key} style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 14px', marginTop: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ fontFamily: PIXEL, fontSize: '7.5px', color: g.tc, transform: 'translateY(0.13em)' }}>
                {g.t}
              </div>
              <div style={{ fontFamily: MONO, fontSize: '9.5px', color: 'var(--gt-muted)' }}>{g.n}</div>
              <div style={{ flex: 1, height: 1, background: 'var(--gt-line)' }} />
            </div>

            {g.rows.map((r) => (
              <div
                key={r.key}
                onClick={() => s.openPtDet(r.id)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid var(--gt-border)',
                  background: 'var(--gt-tile)',
                  cursor: 'pointer',
                }}
              >
                <div style={{ position: 'relative', aspectRatio: '460/160', background: r.fall, overflow: 'hidden' }}>
                  <PtCover style={{ position: 'absolute', inset: 0 }} fs={14} id={r.id} ini={r.ini} cands={r.cands} />
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'linear-gradient(180deg,transparent 40%,rgba(5,5,8,.9))',
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      left: 10,
                      bottom: 9,
                      right: 10,
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 8,
                      flexWrap: 'wrap',
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: 15, color: '#fff' }}>{r.name}</div>
                    <div style={{ fontFamily: MONO, fontSize: 9, color: 'var(--gt-text-4)' }}>{r.dev}</div>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 9, padding: '11px 12px 12px' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 7,
                      fontFamily: MONO,
                      fontSize: 9,
                      letterSpacing: '.04em',
                      flexWrap: 'wrap',
                    }}
                  >
                    {r.wLive ? (
                      <span
                        style={{
                          width: 7,
                          height: 7,
                          background: 'var(--acc)',
                          animation: 'gt-blink 1.1s steps(1) infinite',
                          flex: 'none',
                        }}
                      />
                    ) : null}
                    <span style={{ fontFamily: PIXEL, fontSize: '6.5px', color: r.wLblClr, transform: 'translateY(0.13em)' }}>
                      {r.wLbl}
                    </span>
                    <span style={{ color: 'var(--gt-text)' }}>{r.d1}</span>
                    {r.hasD2 ? (
                      <>
                        <span style={{ color: 'var(--gt-muted)' }}>→</span>
                        <span style={{ color: 'var(--gt-text)' }}>{r.d2}</span>
                      </>
                    ) : null}
                    <span style={{ color: 'var(--gt-muted)' }}>· {r.wFoot}</span>
                  </div>

                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                    {r.chips.map((ch) => (
                      <span
                        key={ch.key}
                        style={{
                          fontFamily: MONO,
                          fontSize: '8.5px',
                          letterSpacing: '.06em',
                          border: '1px solid ' + ch.bd,
                          color: ch.c,
                          background: ch.bg,
                          padding: '3px 6px',
                        }}
                      >
                        {ch.t}
                      </span>
                    ))}
                  </div>

                  <div style={{ fontSize: '11.5px', lineHeight: 1.55, color: 'var(--gt-text-5)' }}>{r.note}</div>

                  <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                    {r.strips.map((st) => (
                      <div key={st.key} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 5,
                            fontFamily: MONO,
                            fontSize: 8,
                            letterSpacing: '.08em',
                            color: st.lc,
                          }}
                        >
                          {st.tri ? <Tri /> : null}
                          {st.l}
                        </div>
                        <div style={{ display: 'flex', gap: 3, alignItems: 'center', minHeight: 18 }}>
                          {st.avs.map((f) => (
                            <Avatar
                              key={f.key}
                              style={{ flex: 'none' }}
                              size={18}
                              fs={6}
                              ini={f.i}
                              bg={f.bg}
                              hasAv={f.hasAv}
                              avBg={f.avBg}
                              title={f.name}
                            />
                          ))}
                          {st.none ? (
                            <span style={{ fontFamily: MONO, fontSize: 10, color: 'var(--gt-ghost)' }}>—</span>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div style={{ display: 'flex', gap: 8 }}>
                    <div
                      onClick={(ev) => {
                        ev.stopPropagation();
                        if (r.pUrl) window.open(r.pUrl, '_blank');
                        // The tab and the question go up together: this click is
                        // the only evidence a request was even attempted, and it
                        // is not evidence that one was SENT (selectors.3e#ptAskVM).
                        if (r.pAsk) s.openPtAsk(r.id);
                      }}
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        border: '1px solid ' + r.pBd,
                        background: r.pBg,
                        color: r.pClr,
                        fontFamily: MONO,
                        fontSize: 10,
                        letterSpacing: '.03em',
                        height: 44,
                        cursor: 'pointer',
                        boxSizing: 'border-box',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                      }}
                    >
                      {r.pTxt}
                    </div>
                    <div
                      onClick={(ev) => {
                        ev.stopPropagation();
                        void s.ptToggleInt(r.id);
                      }}
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                        border: '1px solid ' + r.tBd,
                        background: r.tBg,
                        color: r.tClr,
                        fontFamily: MONO,
                        fontSize: 10,
                        height: 44,
                        cursor: 'pointer',
                        boxSizing: 'border-box',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <Tri w={8} h={7} />
                      {r.tTxt}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}

        {v.hasEndedNote ? (
          <div
            onClick={s.togglePtEnded}
            style={{
              margin: '4px 14px 0',
              border: '1px dashed var(--gt-border)',
              padding: '12px 13px',
              fontFamily: MONO,
              fontSize: '9.5px',
              letterSpacing: '.03em',
              lineHeight: 1.7,
              color: 'var(--gt-muted)',
              cursor: 'pointer',
            }}
          >
            {v.endedNote}
          </div>
        ) : null}

        {v.empty ? (
          <div
            style={{
              padding: '44px 20px',
              textAlign: 'center',
              fontFamily: MONO,
              fontSize: 11,
              color: 'var(--gt-muted)',
              lineHeight: 2,
            }}
          >
            NOTHING MATCHES — CLEAR FILTERS, OR TRACK A TEST VIA + ADD PLAYTEST
          </div>
        ) : null}
      </div>

      <div
        style={{
          borderTop: '1px solid var(--gt-line)',
          padding: '12px 14px',
          fontFamily: MONO,
          fontSize: '8.5px',
          lineHeight: 1.8,
          color: 'var(--gt-muted-2)',
        }}
      >
        {PT_DATA_FOOTER}
      </div>
    </div>
  );
}
